import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Option, SortModel, SubTableColumn } from 'src/components/commons/subTable/types';
import PageInfo from 'src/modules/settings/modules/subTables/types';
import useInfinityDataSource, { FetchFunc } from 'src/hooks/useInfinityDataSource';
import capitalizeFirstWord from 'src/utils/functions/capitalizeFirstWord';
import { AllFilters2, CompanyRankDbModel } from 'src/graphql/schema-types';
import COLUMNS from '../constants';
import { GET_ALL_COMPANY_RANK, GET_COMPANY_RANK_HEADERS } from '../graphql/queries';
import { CompanyRanks, GetAllCompanyRanks, GetCompanyRankHeaders } from '../types';
import {
  CREATE_COMPANY_RANK,
  DELETE_COMPANY_RANK,
  UPDATE_COMPANY_RANK,
} from '../graphql/mutations';
import { filterIDAndAddingTitle } from '../../../../../utils/functions';

const useRankTable = () => {
  const [deleting, setDeleting] = useState(false);
  const [loadData, { loading }] = useLazyQuery<GetAllCompanyRanks>(GET_ALL_COMPANY_RANK, {
    fetchPolicy: 'network-only',
  });
  const [loadDataFilter] = useLazyQuery<GetAllCompanyRanks>(GET_ALL_COMPANY_RANK, {
    fetchPolicy: 'cache-and-network',
  });
  const [deleteMutation] = useMutation(DELETE_COMPANY_RANK);
  const [loadHeaders] = useLazyQuery<GetCompanyRankHeaders>(GET_COMPANY_RANK_HEADERS);
  const [update] = useMutation(UPDATE_COMPANY_RANK);
  const [create] = useMutation(CREATE_COMPANY_RANK);
  const [pagination, setPagination] = useState<PageInfo>({ page: 1, pageSize: 25 });
  const [columns, setColumns] = useState<SubTableColumn[]>([]);
  const [newRecord, setNewRecord] = useState({
    name: '',
  });

  useEffect(() => {
    loadHeaders().then((res) => {
      const filteredResult = filterIDAndAddingTitle(
        res.data?.GetCompanyRankGridHeaders as unknown as SubTableColumn[],
      );
      setColumns(filteredResult ?? COLUMNS);
    });
  }, []);

  const uploadData: FetchFunc = ({ start, renderLen }) =>
    loadData({
      variables: {
        criteria: {
          pagination: {
            from: start,
            size: renderLen,
          },
        },
        searchFields: {
          ids: null,
          names: null,
          enabled: null,
        },
      },
    }).then((res) => ({
      results: res.data?.GetAllCompanyRanks2.results as [],
      total: res.data?.GetAllCompanyRanks2.total as number,
    }));

  const { dataSource, onListRender, reset, setDataSource } = useInfinityDataSource(uploadData);

  const autocompleteFieldSearch = (
    searchField: string,
    value: string,
    dataType: string | number | string[],
  ) =>
    loadDataFilter({
      variables: {
        criteria: {
          pagination: {
            from: 0,
            size: 25,
          },
          filter: [
            {
              fieldName: searchField,
              value,
              dataType,
              operator: AllFilters2.Contains,
            },
          ],
        },
        searchFields: {
          ids: null,
          names: null,
          enabled: null,
        },
      },
    }).then((res) => {
      if (res.data) {
        return {
          results: res?.data?.GetAllCompanyRanks2?.results as any[],
          filter: res?.data?.GetAllCompanyRanks2?.results?.map((result: CompanyRanks) => ({
            value: result[searchField],
            label: result[searchField],
          })) as Option[],
        };
      }

      return {
        results: [],
        filter: [],
      };
    });

  const handleUpdateRows = (rows: CompanyRankDbModel[]) => {
    rows.forEach((row) => {
      const { id, name } = row;
      update({
        variables: {
          updateCompanyRankInput: {
            id,
            name: capitalizeFirstWord(name!),
          },
        },
      });
    });
  };

  const postData = (args: Record<string, unknown>) => {
    setNewRecord((prev) => ({
      ...prev,
      [args?.dataIndex as string]: args?.value as string,
      autoSave: !!args?.autoSave,
    }));
  };

  const resetNewRecord = () => {
    setNewRecord({
      name: '',
    });
  };

  const createNewRecord = async () => {
    const { name } = newRecord;
    if (name === '') return;
    await create({
      variables: {
        createCompanyRankInput: {
          name: capitalizeFirstWord(name),
        },
      },
    }).then((customType) => {
      setDataSource([
        { id: customType.data.CreateCompanyRank.id, name: customType.data.CreateCompanyRank.name },
        ...dataSource,
      ]);
    });
    resetNewRecord();
  };

  const handleDeleteRows = async (rows: CompanyRankDbModel[]) => {
    setDeleting(true);
    await Promise.all(rows.map((row) => deleteMutation({ variables: { id: row.id } })));
    setDeleting(false);
    const ids = rows.map((row) => row.id);
    const copiedDataSource = [...dataSource].filter((data) => !ids.includes(data.id));
    setDataSource(copiedDataSource);
  };

  const handleClearFilter = () => {
    reset();
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize });
  };

  const handleFilterChange = (newFilters: Record<string, string[]>) => {
    setPagination({ ...pagination, filters: newFilters, page: 1 });
  };

  const handleSortModelChange = (sortModel: SortModel | undefined) => {
    setPagination({ ...pagination, sort: sortModel });
  };

  return {
    autocompleteFieldSearch,
    handlePaginationChange,
    handleFilterChange,
    handleSortModelChange,
    pagination,
    columns,
    setColumns,
    handleUpdateRows,
    newRecord,
    postData,
    createNewRecord,
    resetNewRecord,
    spinning: loading || deleting,
    dataSource,
    onListRender,
    handleClearFilter,
    handleDeleteRows,
  };
};

export default useRankTable;
